import { getUiLanguage } from '../config';

const SONIOX_WS_URL = 'wss://stt-rt.soniox.com/transcribe-websocket';

// Errors surfaced to the learner follow the interface language (callers may override).
const MESSAGES = {
  'zh-TW': {
    tokenFailed: '無法取得即時語音辨識權限',
    connectionError: '即時語音連線發生錯誤',
    connectionClosed: '即時語音連線已中斷',
  },
  en: {
    tokenFailed: 'Could not get access to live speech recognition',
    connectionError: 'The live audio connection ran into an error',
    connectionClosed: 'The live audio connection was interrupted',
  },
};
const TARGET_RATE = 16000;

export class LiveStt {
  constructor(callbacks) {
    this.callbacks = callbacks;
    this.tokenUrl = callbacks.tokenUrl || '/api/live-practice/stt-token';
    this.languageHints = callbacks.languageHints || ['zh'];
    this.messages = { ...MESSAGES[getUiLanguage() === 'en' ? 'en' : 'zh-TW'], ...(callbacks.messages || {}) };
    this.ws = null;
    this.audioContext = null;
    this.stream = null;
    this.workletNode = null;
    this.finalTokens = [];
    this.stopped = false;
    this.paused = false;
    this.manual = false;
  }

  async start() {
    this.stopped = false;
    const tokenResponse = await fetch(this.tokenUrl, {
      method: 'POST',
    });
    if (!tokenResponse.ok) {
      throw new Error(this.messages.tokenFailed);
    }
    const { api_key: apiKey } = await tokenResponse.json();

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      },
    });

    this.audioContext = new AudioContext();
    await this.audioContext.resume();
    const source = this.audioContext.createMediaStreamSource(this.stream);
    const workletCode = `
      class PcmTap extends AudioWorkletProcessor {
        process(inputs) {
          const channel = inputs[0] && inputs[0][0];
          if (channel) this.port.postMessage(channel.slice(0));
          return true;
        }
      }
      registerProcessor('pcm-tap', PcmTap);
    `;
    const blobUrl = URL.createObjectURL(
      new Blob([workletCode], { type: 'application/javascript' })
    );
    await this.audioContext.audioWorklet.addModule(blobUrl);
    URL.revokeObjectURL(blobUrl);
    this.workletNode = new AudioWorkletNode(this.audioContext, 'pcm-tap');
    source.connect(this.workletNode);

    const ws = new WebSocket(SONIOX_WS_URL);
    this.ws = ws;
    const sourceRate = this.audioContext.sampleRate;

    ws.onopen = () => {
      ws.send(JSON.stringify({
        api_key: apiKey,
        model: 'stt-rt-v5',
        audio_format: 'pcm_s16le',
        sample_rate: TARGET_RATE,
        num_channels: 1,
        language_hints: this.languageHints,
        enable_endpoint_detection: true,
      }));
      this.callbacks.onReady?.();
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message.error_message) {
          this.callbacks.onError?.(message.error_message);
          return;
        }
        let sawEnd = false;
        let sawSpeech = false;
        for (const token of message.tokens || []) {
          if (token.text === '<end>') {
            sawEnd = true;
            continue;
          }
          if (token.text === '<fin>') continue;
          sawSpeech = true;
          if (token.is_final) this.finalTokens.push(token);
        }
        if (sawSpeech && !this.paused) this.callbacks.onSpeaking?.();
        if (!this.paused) {
          const partial = this.joinTokens(this.finalTokens);
          if (partial) this.callbacks.onPartial?.(partial);
        }
        if (sawEnd && !this.manual) this.flushUtterance();
      } catch (error) {
        console.warn('Unable to parse live transcription event', error);
      }
    };

    ws.onerror = () => this.callbacks.onError?.(this.messages.connectionError);
    ws.onclose = (event) => {
      if (!this.stopped && event.code !== 1000) {
        this.callbacks.onError?.(this.messages.connectionClosed);
      }
    };

    this.workletNode.port.onmessage = (event) => {
      if (this.paused || this.stopped || ws.readyState !== WebSocket.OPEN) return;
      ws.send(this.downsampleToPcm16(event.data, sourceRate));
    };
  }

  joinTokens(tokens) {
    return tokens
      .map((token) => token.text)
      .join('')
      .replace(/\s+/g, ' ')
      .trim();
  }

  flushUtterance() {
    const text = this.joinTokens(this.finalTokens);
    this.finalTokens = [];
    if (text) this.callbacks.onUtterance?.(text);
  }

  downsampleToPcm16(input, sourceRate) {
    const ratio = sourceRate / TARGET_RATE;
    const output = new Int16Array(Math.floor(input.length / ratio));
    for (let index = 0; index < output.length; index += 1) {
      const value = input[Math.floor(index * ratio)];
      output[index] = Math.max(-1, Math.min(1, value)) * 0x7fff;
    }
    return output.buffer;
  }

  setPaused(paused) {
    this.paused = paused;
    if (paused) this.finalTokens = [];
  }

  finalize() {
    setTimeout(() => this.flushUtterance(), 600);
  }

  stop() {
    this.stopped = true;
    this.workletNode?.port.close();
    this.workletNode?.disconnect();
    this.ws?.close(1000);
    this.stream?.getTracks().forEach((track) => track.stop());
    this.audioContext?.close().catch(() => {});
    this.ws = null;
    this.stream = null;
    this.audioContext = null;
  }
}
